/**
 * TanStack Query hook for GET /api/v1/me/quotas.
 *
 * - Refetches every 60s in the background.
 * - Wire is `{ quotas, envelope }` (FEAT-036); both are synced to useQuotaStore
 *   (derived flags isWarning/isCritical/isExceeded + mostRestrictive).
 * - Provides invalidateQuotas for SSE-triggered immediate refetch.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { quotasApi, type QuotaEnvelope, type TokenUsage } from '@/services/api/quotas';
import { useQuotaStore } from '@/stores/quotaStore';

// ---------------------------------------------------------------------------
// Query Keys
// ---------------------------------------------------------------------------

export const quotaKeys = {
  all: ['quotas'] as const,
  mine: () => [...quotaKeys.all, 'mine'] as const,
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseMyQuotasReturn {
  quotas: TokenUsage[];
  envelope: QuotaEnvelope | null;
  isLoading: boolean;
  error: Error | null;
  /** Trigger an immediate refetch (e.g., after SSE quota_warning event) */
  invalidateQuotas: () => void;
}

export function useMyQuotas(): UseMyQuotasReturn {
  const queryClient = useQueryClient();
  const setQuotas = useQuotaStore((s) => s.setQuotas);
  const setEnvelope = useQuotaStore((s) => s.setEnvelope);
  const setQuotasRef = useRef(setQuotas);
  setQuotasRef.current = setQuotas;
  const setEnvelopeRef = useRef(setEnvelope);
  setEnvelopeRef.current = setEnvelope;

  const { data, isLoading, error } = useQuery({
    queryKey: quotaKeys.mine(),
    queryFn: () => quotasApi.getMyQuotas(),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  // Sync to Zustand store whenever data changes
  useEffect(() => {
    if (data?.quotas) {
      setQuotasRef.current(data.quotas);
    }
    if (data?.envelope) {
      setEnvelopeRef.current(data.envelope);
    }
  }, [data]);

  const invalidateQuotas = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: quotaKeys.mine() });
  }, [queryClient]);

  return {
    quotas: data?.quotas ?? [],
    envelope: data?.envelope ?? null,
    isLoading,
    error: error as Error | null,
    invalidateQuotas,
  };
}
